import { useEffect, useState } from "react";
import OwnerMoodsChart, {
	type MessageSheetOwnerMood,
} from "./OwnerMoodsChart.tsx";
import { SafeHtml } from "./SafeHtml.tsx";
import { MobileContentHero } from "../mobile/mobilePrimitives.tsx";
import { helpers } from "../util/helpers.ts";
import i18n from "../util/i18n.ts";
import {
	translateMessageFrom,
	translateMessageHtml,
} from "../util/messageTranslations.ts";
import { Trans } from "../util/useT.ts";
import { toWorker } from "../util/toWorker.ts";
import {
	getInboxMessageBusinessAction,
	getInboxMessageDecisionContext,
} from "../views/messageDisplay.ts";

// =============================================================================
// G4-2: the owner letter, extracted from InboxSheet's level-2 so the SAME
// implementation renders in two containers (D8 三容器):
//   - the InboxSheet "tool" bottom sheet (archive browsing, level 2), and
//   - the OwnerLetterDialog "decision" centered dialog (a pending letter pushed
//     straight to the player from the home queue / ?m_open_latest deep link).
// The letter body IS the first-screen content; the sender/year 落款 sits at the
// body tail (letter feel), no longer folded behind a "消息详情" disclosure.
// =============================================================================

export type MessageSheetLetter = {
	from: string;
	mid: number;
	read: boolean;
	subject: string | undefined;
	text: string;
	tid: number | undefined;
	year: number;
	ownerMoods: MessageSheetOwnerMood[] | undefined;
};

// Shared letter fetch + mark-as-read side effect. getMessageSheet flips read,
// persists it and refreshes the play menu server-side, reproducing the old
// message page-view. Returns undefined while loading, null when not found.
export const useOwnerLetter = (mid: number | undefined, open: boolean) => {
	const [letter, setLetter] = useState<MessageSheetLetter | undefined | null>(
		null,
	);

	useEffect(() => {
		if (!open || mid === undefined) {
			setLetter(null);
			return;
		}

		let cancelled = false;
		setLetter(undefined);
		(async () => {
			const result = await toWorker("main", "getMessageSheet", mid);
			if (!cancelled) {
				setLetter(result.message ?? null);
			}
		})();

		return () => {
			cancelled = true;
		};
	}, [mid, open]);

	return letter;
};

// The business CTA for a letter, or undefined when the letter needs no decision
// (a "决定" container renders exactly one main action, only for decision letters;
// updates/records get none — the container's X is the only control). NAVIGATE
// exit to the closest business surface, per the sheet/dialog boundary contract.
export const getOwnerLetterDecisionCta = (letter: MessageSheetLetter) => {
	const decisionContext = getInboxMessageDecisionContext(letter);
	if (decisionContext.kind !== "decision") {
		return undefined;
	}

	const businessAction = getInboxMessageBusinessAction(letter);
	return {
		defaultLabel: businessAction.defaultLabel,
		href: helpers.leagueUrl(businessAction.path),
		labelKey: businessAction.labelKey,
	};
};

export const OwnerLetterContent = ({
	message,
}: {
	message: MessageSheetLetter | undefined;
}) => {
	if (!message) {
		return (
			<div data-v5-message-not-found="">
				<p className="mb-0">
					{i18n.t("message.notFoundBody", {
						ns: "ui",
						defaultValue:
							"This message is no longer available. Messages appear after owner updates, season summaries, and other league events.",
					})}
				</p>
			</div>
		);
	}

	return (
		<div data-v5-message-profile="">
			{/* v5Content: the owner's letter IS the first-screen content — the body
			    plus the owner-trust history chart, with the sender/year 落款 as the
			    letter's closing line (not a folded "消息详情" depth anymore). */}
			<MobileContentHero
				data-v5-message-content-first-screen=""
				data-v5-message-letter-first-screen=""
			>
				<div className="mobile-message-body" data-v5-message-body="">
					<SafeHtml dirty={translateMessageHtml(message.text)} />
				</div>

				{message.ownerMoods && message.ownerMoods.length > 0 ? (
					<div data-v5-message-owner-moods="">
						<OwnerMoodsChart ownerMoods={message.ownerMoods} />
					</div>
				) : null}

				<p
					className="mobile-message-signoff text-body-secondary mt-3 mb-0"
					data-v5-message-signoff=""
				>
					<Trans
						i18nKey="message.from"
						ns="ui"
						defaults="<bold>From: {{from}}</bold>, {{year}}"
						components={{ bold: <b /> }}
						values={{
							from: translateMessageFrom(message.from),
							year: message.year,
						}}
					/>
				</p>
			</MobileContentHero>
		</div>
	);
};
